import { useEffect, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, MessageCircle, Radio, Users } from "lucide-react";
import api from "@/lib/api";
import { trackClick, waDigits } from "@/lib/track";

const FALLBACK = {
  whatsapp_number: "+1 330-988-5582",
  whatsapp_channel: "https://whatsapp.com/channel/0029VbDCy5W8fewjwIqD9m2Z",
  whatsapp_community: "https://chat.whatsapp.com/KnP1PHIhMikKzhSTGBS2ga",
};

const PREFILL = "Hi ROAMIES! 👋 I'm interested in learning more about your upcoming group adventures.";

export const openWhatsAppHub = () => window.dispatchEvent(new CustomEvent("roamies:open-whatsapp-hub"));

export const WhatsAppHub = () => {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(FALLBACK);

  useEffect(() => {
    const listener = () => setOpen(true);
    window.addEventListener("roamies:open-whatsapp-hub", listener);
    api.get("/settings/public").then((r) => setCfg((c) => ({ ...c, ...r.data }))).catch(() => {});
    return () => window.removeEventListener("roamies:open-whatsapp-hub", listener);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const go = useCallback((action, url) => {
    trackClick(action);
    window.open(url, "_blank", "noopener,noreferrer");
    setOpen(false);
  }, []);

  const chatUrl = `https://wa.me/${waDigits(cfg.whatsapp_number)}?text=${encodeURIComponent(PREFILL)}`;

  const options = [
    { id: "chat", icon: MessageCircle, title: "Chat With Us", desc: "Questions about ROAMIES? Message our team.",
      action: () => go("whatsapp_chat", chatUrl), testid: "whatsapp-option-chat" },
    { id: "channel", icon: Radio, title: "Follow Our Channel", desc: "Get trip announcements, updates and ROAMIES news.",
      action: () => go("whatsapp_channel", cfg.whatsapp_channel || FALLBACK.whatsapp_channel), testid: "whatsapp-option-channel" },
    { id: "community", icon: Users, title: "Join Our Community", desc: "Meet travelers, stay connected and be part of the ROAMIES community.",
      action: () => go("whatsapp_community", cfg.whatsapp_community || FALLBACK.whatsapp_community), testid: "whatsapp-option-community" },
  ];

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-ink/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-4"
          onClick={() => setOpen(false)} data-testid="whatsapp-community-modal">
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.98 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="bg-cream text-ink border-2 border-ink shadow-[6px_6px_0_0_#ff4500] max-w-md w-full p-6 sm:p-8 relative"
            onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setOpen(false)} aria-label="Close WhatsApp menu" data-testid="whatsapp-hub-close"
              className="absolute top-4 right-4 p-2 text-ink/50 hover:text-flame transition-colors">
              <X className="w-5 h-5" />
            </button>
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-flame mb-2">ROAMIES on WhatsApp</p>
            <p className="font-display text-3xl uppercase leading-[0.95] mb-2">Pick your way in.</p>
            <p className="text-sm text-ink/60 mb-6">Chat with the team, follow the drop alerts, or meet the community.</p>
            <div className="space-y-3">
              {options.map((o) => (
                <button key={o.id} onClick={o.action} data-testid={o.testid}
                  className="w-full text-left p-4 border border-ink/15 bg-white hover:border-flame hover:bg-flame hover:text-white transition-colors duration-200 flex items-center gap-4 group">
                  <span className="p-2.5 border border-ink/15 group-hover:border-white/40 text-flame group-hover:text-white transition-colors">
                    <o.icon className="w-5 h-5" />
                  </span>
                  <span>
                    <span className="block font-display text-lg uppercase tracking-tight">{o.title}</span>
                    <span className="block text-xs opacity-70 mt-0.5">{o.desc}</span>
                  </span>
                </button>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
